import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100"></div>
      
      {/* Animated background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-48 md:w-72 h-48 md:h-72 bg-[#FF6B6B]/10 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-64 md:w-96 h-64 md:h-96 bg-[#0A1628]/5 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-gradient-to-r from-[#FF6B6B]/5 to-[#0A1628]/5 rounded-full filter blur-3xl animate-spin" style={{ animationDuration: '30s' }}></div>
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-32 right-[15%] w-4 h-4 bg-[#FF6B6B] rounded-full animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-48 right-[25%] w-6 h-6 border-2 border-[#0A1628]/20 rounded-full animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-40 left-[20%] w-3 h-3 bg-[#0A1628]/30 rotate-45 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1s' }}></div>
        <div className="absolute top-60 left-[10%] w-8 h-8 border-2 border-[#FF6B6B]/30 rounded-lg rotate-12 animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 py-24 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className={`space-y-6 md:space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 bg-[#0A1628]/5 backdrop-blur-sm px-4 py-2 rounded-full">
              <span className="w-2 h-2 bg-[#FF6B6B] rounded-full animate-pulse"></span>
              <span className="text-xs sm:text-sm text-[#0A1628]/70 font-medium">Freelance Web Designer</span>
            </div>

            <div className="space-y-4 md:space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-[#0A1628] leading-tight">
                Websites for<br />
                <span className="text-[#FF6B6B] italic">small business</span>
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-[#0A1628]/60 font-light tracking-wide max-w-lg">
                Professional website creation in Australia. Custom sites for local businesses, artisans & entrepreneurs
              </p>
            </div>

            <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="group bg-[#FF6B6B] text-white px-6 sm:px-8 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#ff5252] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                Get your website - $399
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-transparent border-2 border-[#0A1628] text-[#0A1628] px-6 sm:px-8 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#0A1628] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Contact me
              </button>
            </div>

            {/* Stats */}
            <div className={`flex flex-wrap gap-8 sm:gap-12 pt-6 md:pt-8 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div>
                <span className="text-3xl sm:text-4xl font-bold text-[#0A1628]">5+</span>
                <p className="text-xs sm:text-sm text-[#0A1628]/50">years experience</p>
              </div>
              <div>
                <span className="text-3xl sm:text-4xl font-bold text-[#0A1628]">50+</span>
                <p className="text-xs sm:text-sm text-[#0A1628]/50">projects completed</p>
              </div>
              <div>
                <span className="text-3xl sm:text-4xl font-bold text-[#FF6B6B]">100%</span>
                <p className="text-xs sm:text-sm text-[#0A1628]/50">satisfaction</p>
              </div>
            </div>
          </div>

          {/* Simple laptop/website mockup illustration */}
          <div className={`relative hidden lg:flex items-center justify-center transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="relative w-full h-[500px] flex items-center justify-center">
              
              {/* Laptop frame */}
              <div className="relative">
                {/* Screen */}
                <div className="w-[420px] h-[280px] bg-[#0A1628] rounded-2xl p-3 shadow-2xl">
                  {/* Browser window */}
                  <div className="w-full h-full bg-white rounded-lg overflow-hidden">
                    {/* Browser bar */}
                    <div className="h-8 bg-gray-100 flex items-center px-3 gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 bg-[#FF6B6B] rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="bg-white rounded-full h-5 px-3 flex items-center">
                          <span className="text-[10px] text-gray-400">yourwebsite.com</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Website content mockup */}
                    <div className="p-4 space-y-4">
                      {/* Nav */}
                      <div className="flex items-center justify-between">
                        <div className="w-16 h-4 bg-[#0A1628] rounded"></div>
                        <div className="flex gap-3">
                          <div className="w-10 h-2 bg-gray-200 rounded"></div>
                          <div className="w-10 h-2 bg-gray-200 rounded"></div>
                          <div className="w-10 h-2 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                      
                      {/* Hero section */}
                      <div className="flex gap-4 mt-6">
                        <div className="flex-1 space-y-3">
                          <div className="w-3/4 h-4 bg-[#0A1628] rounded"></div>
                          <div className="w-full h-3 bg-gray-200 rounded"></div>
                          <div className="w-2/3 h-3 bg-gray-200 rounded"></div>
                          <div className="w-20 h-6 bg-[#FF6B6B] rounded-full mt-4"></div>
                        </div>
                        <div className="w-24 h-24 bg-gradient-to-br from-[#FF6B6B]/20 to-[#0A1628]/10 rounded-xl"></div>
                      </div>
                      
                      {/* Cards */}
                      <div className="flex gap-3 mt-4">
                        <div className="flex-1 h-16 bg-gray-50 rounded-lg border border-gray-100 p-2">
                          <div className="w-6 h-6 bg-[#FF6B6B]/20 rounded mb-1"></div>
                          <div className="w-full h-2 bg-gray-200 rounded"></div>
                        </div>
                        <div className="flex-1 h-16 bg-gray-50 rounded-lg border border-gray-100 p-2">
                          <div className="w-6 h-6 bg-[#0A1628]/10 rounded mb-1"></div>
                          <div className="w-full h-2 bg-gray-200 rounded"></div>
                        </div>
                        <div className="flex-1 h-16 bg-gray-50 rounded-lg border border-gray-100 p-2">
                          <div className="w-6 h-6 bg-[#FF6B6B]/20 rounded mb-1"></div>
                          <div className="w-full h-2 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Laptop base */}
                <div className="w-[480px] h-4 bg-gradient-to-b from-[#d1d5db] to-[#9ca3af] rounded-b-xl mx-auto -mt-1"></div>
                <div className="w-[520px] h-2 bg-[#9ca3af] rounded-b-3xl mx-auto shadow-lg"></div>
              </div>

              {/* Floating elements */}
              <div className="absolute top-8 right-8 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center animate-bounce" style={{ animationDuration: '3s' }}>
                <i className="ri-code-s-slash-line text-xl text-[#FF6B6B]"></i>
              </div>
              
              <div className="absolute bottom-20 left-4 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                <i className="ri-layout-4-line text-xl text-[#0A1628]"></i>
              </div>
              
              <div className="absolute top-1/3 left-0 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center animate-pulse">
                <i className="ri-cursor-line text-lg text-[#FF6B6B]"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#0A1628]/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#FF6B6B] rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
